<template>
  <div class="pw-loader" role="status" aria-live="polite" aria-busy="true">
    <div class="pw-loader__ambient" aria-hidden="true">
      <div class="pw-loader__orb pw-loader__orb--1" />
      <div class="pw-loader__orb pw-loader__orb--2" />
      <div class="pw-loader__orb pw-loader__orb--3" />
      <div class="pw-loader__grid" />
    </div>

    <div class="pw-loader__panel">
      <div class="pw-loader__visual" aria-hidden="true">
        <div class="pw-loader__orbit pw-loader__orbit--outer">
          <span class="pw-loader__satellite pw-loader__satellite--a" />
        </div>
        <div class="pw-loader__orbit pw-loader__orbit--mid">
          <span class="pw-loader__satellite pw-loader__satellite--b" />
        </div>
        <div class="pw-loader__orbit pw-loader__orbit--inner" />
        <div class="pw-loader__core">
          <span class="pw-loader__core-ring" />
          <span class="pw-loader__core-dot" />
        </div>
      </div>

      <div class="pw-loader__copy">
        <p class="pw-loader__title">{{ title }}</p>
        <p class="pw-loader__subtitle">
          <span>{{ subtitle }}</span>
          <span class="pw-loader__dots" aria-hidden="true">
            <i /><i /><i />
          </span>
        </p>
      </div>

      <div class="pw-loader__track" aria-hidden="true">
        <span class="pw-loader__track-fill" />
      </div>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: 'PortalWorkspaceLoader' })

defineProps({
  title: { type: String, default: 'Loading your workspace' },
  subtitle: { type: String, default: 'Fetching your account details' },
})
</script>

<style scoped>
.pw-loader {
  --pw-accent: #2563eb;
  --pw-accent-soft: #6366f1;
  --pw-glow: rgba(37, 99, 235, 0.45);

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  overflow: hidden;
  background: var(--y-bg, #030712);
  color: var(--y-text, #f8fafc);
}

.pw-loader__ambient {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.pw-loader__orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(72px);
  opacity: 0.55;
  animation: pw-float 14s ease-in-out infinite;
}

.pw-loader__orb--1 {
  width: 420px;
  height: 420px;
  top: -12%;
  left: -8%;
  background: radial-gradient(circle, rgba(37, 99, 235, 0.55) 0%, transparent 70%);
}

.pw-loader__orb--2 {
  width: 360px;
  height: 360px;
  right: -10%;
  bottom: -8%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.45) 0%, transparent 72%);
  animation-delay: -4s;
}

.pw-loader__orb--3 {
  width: 280px;
  height: 280px;
  top: 42%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(5, 68, 226, 0.35) 0%, transparent 70%);
  animation-delay: -8s;
}

.pw-loader__grid {
  position: absolute;
  inset: 0;
  opacity: 0.22;
  background-image:
    linear-gradient(rgba(148, 163, 184, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.08) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(circle at center, black 20%, transparent 78%);
}

.pw-loader__panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  width: min(100%, 380px);
  padding: 36px 32px 28px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(8, 14, 32, 0.62);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 24px 64px rgba(2, 6, 23, 0.55),
    0 0 80px rgba(37, 99, 235, 0.12);
}

.pw-loader__visual {
  position: relative;
  width: 112px;
  height: 112px;
}

.pw-loader__orbit {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid transparent;
}

.pw-loader__orbit--outer {
  inset: 0;
  border-top-color: rgba(96, 165, 250, 0.85);
  border-right-color: rgba(96, 165, 250, 0.15);
  animation: pw-spin 2.8s cubic-bezier(0.45, 0.05, 0.25, 1) infinite;
}

.pw-loader__orbit--mid {
  inset: 14px;
  border-bottom-color: rgba(129, 140, 248, 0.9);
  border-left-color: rgba(129, 140, 248, 0.12);
  animation: pw-spin-reverse 2.1s cubic-bezier(0.45, 0.05, 0.25, 1) infinite;
}

.pw-loader__orbit--inner {
  inset: 28px;
  border-top-color: rgba(59, 130, 246, 0.55);
  border-right-color: rgba(59, 130, 246, 0.08);
  animation: pw-spin 1.6s linear infinite;
}

.pw-loader__satellite {
  position: absolute;
  border-radius: 50%;
  box-shadow: 0 0 12px var(--pw-glow);
}

.pw-loader__satellite--a {
  top: -3px;
  left: 50%;
  width: 7px;
  height: 7px;
  margin-left: -3.5px;
  background: #93c5fd;
}

.pw-loader__satellite--b {
  bottom: 8px;
  right: 8px;
  width: 5px;
  height: 5px;
  background: #a5b4fc;
}

.pw-loader__core {
  position: absolute;
  inset: 38px;
  display: grid;
  place-items: center;
}

.pw-loader__core-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(
    from 180deg,
    rgba(37, 99, 235, 0.05),
    rgba(37, 99, 235, 0.55),
    rgba(99, 102, 241, 0.15),
    rgba(37, 99, 235, 0.05)
  );
  animation: pw-spin 3.5s linear infinite;
  mask: radial-gradient(circle, transparent 58%, black 60%);
  -webkit-mask: radial-gradient(circle, transparent 58%, black 60%);
}

.pw-loader__core-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #60a5fa, #6366f1);
  box-shadow: 0 0 18px rgba(96, 165, 250, 0.85);
  animation: pw-pulse 1.8s ease-in-out infinite;
}

.pw-loader__copy {
  text-align: center;
}

.pw-loader__title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  font-weight: 650;
  letter-spacing: -0.03em;
  background: linear-gradient(90deg, #f8fafc 0%, #dbeafe 45%, #f8fafc 90%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: pw-shimmer 3s linear infinite;
}

.pw-loader__subtitle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin: 0;
  font-size: 0.875rem;
  color: var(--y-text-muted, rgba(248, 250, 252, 0.62));
  line-height: 1.5;
}

.pw-loader__dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 2px;
}

.pw-loader__dots i {
  display: block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(147, 197, 253, 0.9);
  animation: pw-dot 1.2s ease-in-out infinite;
}

.pw-loader__dots i:nth-child(2) {
  animation-delay: 0.15s;
}

.pw-loader__dots i:nth-child(3) {
  animation-delay: 0.3s;
}

.pw-loader__track {
  width: 100%;
  height: 3px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.14);
}

.pw-loader__track-fill {
  display: block;
  width: 42%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, transparent, #60a5fa, #818cf8, transparent);
  animation: pw-slide 1.8s ease-in-out infinite;
}

@keyframes pw-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pw-spin-reverse {
  to {
    transform: rotate(-360deg);
  }
}

@keyframes pw-pulse {
  0%,
  100% {
    transform: scale(0.92);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
}

@keyframes pw-shimmer {
  to {
    background-position: 200% center;
  }
}

@keyframes pw-dot {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.35;
  }
  40% {
    transform: translateY(-3px);
    opacity: 1;
  }
}

@keyframes pw-slide {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(320%);
  }
}

@keyframes pw-float {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(0, -18px, 0) scale(1.04);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pw-loader__orb,
  .pw-loader__orbit,
  .pw-loader__core-ring,
  .pw-loader__core-dot,
  .pw-loader__title,
  .pw-loader__dots i,
  .pw-loader__track-fill {
    animation: none !important;
  }

  .pw-loader__title {
    color: #f8fafc;
    background: none;
    -webkit-background-clip: unset;
    background-clip: unset;
  }
}
</style>
